

/*
 * Land cover color mappings
 */
export const LAND_COVER_COLORS = {
  'sealed': '#e74c3c',
  'permanent herbaceous': '#2ecc71',
  'woodland': '#27ae60',
  'water': '#3498db',
  'bare soil': '#d35400',
  'arable land': '#f39c12',
  'urban': '#95a5a6',
  'industrial': '#7f8c8d'
}

/**
 * Soil type color mappings
 */
export const SOIL_TYPE_COLORS = {
  'calcaric fluvisols': '#8b4513',
  'eutric cambisols': '#a0522d',
  'calcic cambisols': '#cd853f',
  'folic leptosols': '#6b4423',
  'haplic luvisols': '#b8860b',
  'rendzic leptosols': '#8b7355',
  'chromic cambisols': '#d2691e',
  'vertic cambisols': '#a0826d',
  'skeletic leptosols': '#9c7c6c',
  'mollic gleysols': '#7a5f4d',
  'dystric cambisols': '#8b6914',
  'umbric gleysols': '#5c4033'
}

/**
 * Unified color map combining all classes
 */
export const CLASS_COLORS = {
  ...LAND_COVER_COLORS,
  ...SOIL_TYPE_COLORS
}

/**
 * Class name display mappings
 */
export const CLASS_NAMES = {
  'sealed': 'Sealed Surface',
  'permanent herbaceous': 'Grassland',
  'woodland': 'Forest',
  'calcaric fluvisols': 'Calcaric Fluvisols',
  'eutric cambisols': 'Eutric Cambisols',
  'calcic cambisols': 'Calcic Cambisols',
  'folic leptosols': 'Folic Leptosols',
  'haplic luvisols': 'Haplic Luvisols',
  'calaric leptosols': 'Calcaric Leptosols'
}

/**
 * Generate consistent color from string using hash
 * @param {string} str - Input string
 * @returns {string} HSL color string
 */
export function generateColorFromString(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 60%, 50%)`
}

/**
 * Generate soil-type specific color (brown shades)
 * @param {string} str - Soil type name
 * @returns {string} HSL color string
 */
export function generateSoilColor(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = 20 + (Math.abs(hash) % 20) // Brown range: 20-40
  const saturation = 40 + (Math.abs(hash >> 8) % 30) // 40-70%
  const lightness = 30 + (Math.abs(hash >> 16) % 20) // 30-50%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

/**
 * Generate vegetation color (green shades)
 * @param {string} str - Vegetation class name
 * @returns {string} HSL color string
 */
export function generateVegetationColor(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = 90 + (Math.abs(hash) % 60) // Green range: 90-150
  const saturation = 50 + (Math.abs(hash >> 8) % 30) // 50-80%
  const lightness = 35 + (Math.abs(hash >> 16) % 20) // 35-55%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

/**
 * Get color for any class with fallback logic
 * @param {string} className - Class name
 * @param {Object} customColorMap - Optional custom color map
 * @returns {string} Color string
 */
export function getColorForClass(className, customColorMap = null) {
  // Check custom map first
  if (customColorMap && customColorMap[className]) {
    return customColorMap[className]
  }
  
  // Check default maps
  if (CLASS_COLORS[className]) {
    return CLASS_COLORS[className]
  }
  
  // Generate color based on category
  const lowerClass = String(className).toLowerCase()
  
  // Soil types
  if (lowerClass.includes('cambisol') || 
      lowerClass.includes('leptosol') || 
      lowerClass.includes('luvisol') ||
      lowerClass.includes('fluvisol') ||
      lowerClass.includes('gleysol')) {
    return generateSoilColor(className)
  }
  
  // Vegetation
  if (lowerClass.includes('herbaceous') || 
      lowerClass.includes('woodland') || 
      lowerClass.includes('grass') ||
      lowerClass.includes('forest')) {
    return generateVegetationColor(className)
  }
  
  // Generic fallback
  return generateColorFromString(className)
}

/**
 * Format class name for display
 * @param {string} className - Raw class name
 * @returns {string} Formatted class name
 */
export function formatClassName(className) {
  if (CLASS_NAMES[className]) {
    return CLASS_NAMES[className]
  }
  
  // Capitalize each word
  if (typeof className === 'string') {
    return className.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  
  return className
}